import { MutationResult } from '@apollo/client';
import {
  Stripe,
  StripeCardNumberElement,
  StripeElements,
} from '@stripe/stripe-js';
import { createModel } from 'xstate/lib/model';

import { PackageType, PlansVariant } from '__generated__/GQL';
import baseEvents from 'common/bento/config/baseEvents';
import { ProviderPackageId } from 'types/planUtils';

import { GetPackagePaymentIntentMutation } from '../graphql/mutations/getPackagePaymentIntent.gql';
import { OnboardingPackagesQuery } from '../graphql/queries/onboardingPackages.gql';
import { OnboardingPlansQuery } from '../graphql/queries/onboardingPlans.gql';
import { PricingPlansDataQuery } from '../graphql/queries/pricingPlansData.gql';

export type OnboardingPlanItem =
  OnboardingPlansQuery['viewer']['company']['onboardingPlans'][number];

export type OnboardingPackageItem = Omit<
  OnboardingPackagesQuery['viewer']['company']['onboardingPackages'][number],
  'providerPackageId'
> & {
  providerPackageId: ProviderPackageId;
};

export type PricingPlansModuleData = PricingPlansDataQuery & {
  packageType: PackageType | null;
  shouldShowPackagePriceWithVAT: boolean;
  stripe: Stripe;
  isMobile: boolean;
};

export interface PaymentData {
  paymentIntentId: string | null;
  clientSecret: string | null;
  readyForCapture: boolean | null;
  setupIntentId: string | null;
}

export type Context = {
  getModuleData: () => PricingPlansModuleData;
  selectedPlan: Pick<
    OnboardingPlanItem,
    'brandName' | 'features' | 'providerPlanId'
  > | null;
  selectedPackage: Pick<OnboardingPackageItem, 'providerPackageId'> | null;
  selectedCoupon: string | null;
  selectedVariant: PlansVariant | null;
  paymentData: PaymentData | null;
};

const initialContext: Context = {
  getModuleData: () => {
    throw new Error('NOT_IMPLEMENTED');
  },
  paymentData: null,
  selectedCoupon: null,
  selectedPackage: null,
  selectedPlan: null,
  selectedVariant: null,
};

const creators = {
  events: {
    ...baseEvents,
    ABORT: () => ({}),
    'done.invoke.getPaymentIntent': (
      data: MutationResult<GetPackagePaymentIntentMutation>,
    ) => ({ data }),
    PAY: (
      data:
        | { cardNumberElement: StripeCardNumberElement }
        | { elements: StripeElements },
    ) => ({ data }),
    SAVE_SELECTED_PACKAGE: (data: OnboardingPackageItem) => ({ data }),
    SAVE_SELECTED_PLAN: (data: OnboardingPlanItem) => ({ data }),
    SAVE_VARIANT: (data: PlansVariant | null) => ({ data }),
    SHOW_DETAILS: () => ({}),
    SUBMIT_COUPON: (data: string) => ({ data }),
  },
};

const model = createModel(initialContext, creators);

export type Model = typeof model;
export default model;
